import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { UserSportStatisticFormat } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelS } from '@sorare/core/src/atoms/typography';

import type { ManagerIDStat_userSportStatistic } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ center: true, gap: 0.5 })`
  justify-content: space-between;
  align-self: stretch;
`;

type Props = {
  statistic: ManagerIDStat_userSportStatistic | null;
};
export const ManagerIDStat = ({ statistic }: Props) => {
  const { formatDate, formatNumber } = useIntl();

  if (!statistic) {
    return null;
  }
  return (
    <Root>
      {statistic.format === UserSportStatisticFormat.NUMBER && (
        <>
          <LabelS color="var(--c-nd-600)" className="text-center">
            {statistic.title}
          </LabelS>
          <BodyS bold>
            {formatNumber(statistic.value, {
              compactDisplay: 'short',
              notation: 'compact',
              maximumFractionDigits: 1,
            })}
          </BodyS>
        </>
      )}
      {statistic.format === UserSportStatisticFormat.TIMESTAMP && (
        <>
          <LabelS color="var(--c-nd-600)" className="text-center">
            {statistic.title}
          </LabelS>
          <BodyS bold>
            {formatDate(statistic.value * 1000, {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })}
          </BodyS>
        </>
      )}
    </Root>
  );
};

ManagerIDStat.fragments = {
  userSportStatistic: gql`
    fragment ManagerIDStat_userSportStatistic on UserSportStatistic {
      id
      name
      value
      format
      title
    }
  ` as TypedDocumentNode<ManagerIDStat_userSportStatistic>,
};
